import { useState } from 'react'
import { usePatientProducts } from '@/hooks/useProducts'
import { inHandProducts } from '@/lib/productSupersession'
import type { CreatedProduct } from '@/types/products'
import { useLocationText } from '@/hooks/useStoragePlaces'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { dinSearchText } from '@/lib/isbtDin'

/**
 * Pick one of this patient's products, by DIN or product code.
 *
 * Split and Pool each grew their own copy of this list; Store and Update
 * needed a third and a fourth, which is the point at which it becomes one
 * component. Scanning is the intended input — the field takes a barcode the
 * same way it takes typing, which is why it is a plain text box rather than a
 * dropdown.
 */
export function SelectProductDialog({
  open,
  title,
  description,
  patientMrn,
  onSelect,
  onClose,
}: {
  open: boolean
  title: string
  description: string
  patientMrn: string
  onSelect: (product: CreatedProduct) => void
  onClose: () => void
}) {
  // Only while open: this dialog is always mounted on the case page (z8nrz7e1c2).
  // The patient's products only, filtered by the server: this read every
  // product in the tenant to keep one patient's (z8nrz7fzk7).
  const patientProducts = usePatientProducts(patientMrn || null, open)
  const locate = useLocationText()
  // A write while closed only marks the list stale (a disabled query does not
  // refetch), so an open that is refetching must not show the old list.
  const productsLoading = patientProducts.isLoading || (patientProducts.isFetching && patientProducts.isStale)
  const [search, setSearch] = useState('')

  // Superseded products are history, not bags (z8nrz7e72t), and an original
  // used up by a split has nothing left to store or measure (z8nrz7g2wm).
  const candidates = inHandProducts(patientProducts.data ?? []).filter(p => {
    const q = dinSearchText(search).toLowerCase()
    if (!q) return true
    return p.din.toLowerCase().includes(q) || p.productDescriptionCode.toLowerCase().includes(q)
  })

  function handleOpenChange(next: boolean) {
    if (!next) {
      setSearch('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Scan or type the DIN or product code'
          aria-label='Find product'
          className='font-mono'
          autoFocus
        />

        <div className='max-h-72 overflow-y-auto rounded-lg border'>
          {productsLoading ? (
            <p className='p-4 text-sm text-muted-foreground'>Loading products…</p>
          ) : candidates.length === 0 ? (
            <p className='p-4 text-sm text-muted-foreground'>
              {patientMrn ? 'No products found for this patient.' : 'No patient on this form.'}
            </p>
          ) : (
            candidates.map(p => (
              <button
                key={p.id}
                type='button'
                onClick={() => {
                  setSearch('')
                  onSelect(p)
                }}
                className='flex w-full items-center gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent'
              >
                <span className='font-mono'>{p.din}</span>
                <span className='text-muted-foreground'>{p.productDescriptionCode || '—'}</span>
                <span className='ml-auto text-xs text-muted-foreground'>
                  {p.volumeMl != null ? `${p.volumeMl} mL` : 'no volume'}
                  {p.storagePath ? ` · ${locate(p.storagePath)}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
